import React, { forwardRef, useRef, useEffect, useId } from 'react';
import { createPortal } from 'react-dom';
import { __ } from '../i18n/vsme';


export type ModalProps = {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  className?: string;
  closeOnOverlayClick?: boolean;
  closeOnEscape?: boolean;
  showCloseButton?: boolean;
  size?: 'small' | 'medium' | 'large' | 'full';
  position?: 'center' | 'top' | 'right' | 'bottom' | 'left';
  initialFocusRef?: React.RefObject<HTMLElement | null>;
  returnFocusRef?: React.RefObject<HTMLElement | null>;
  ariaDescribedBy?: string;
  ariaLabelledBy?: string;
  testId?: string;
};

const assignDivRef = (
  targetRef: React.ForwardedRef<HTMLDivElement>,
  value: HTMLDivElement | null
): void => {
  if (typeof targetRef === 'function') {
    targetRef(value);
    return;
  }
  if (!targetRef) {
    return;
  }
  (targetRef as React.MutableRefObject<HTMLDivElement | null>).current = value;
};


const isHTMLElement = (element: Element | null): element is HTMLElement => {
  return element !== null && 'focus' in element;
};

// Filter out elements that are not focusable
const getFocusableElements = (container: HTMLElement): HTMLElement[] => {
  // List of focusable elements
  const selector = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"]), [contenteditable="true"]';
  // Make an array of focusable elements
  const elements = Array.from(container.querySelectorAll<HTMLElement>(selector));
  
  // return the list, except for those that are hidden
  return elements.filter((el) => {
    const style = window.getComputedStyle(el);
    return style.display !== 'none' && style.visibility !== 'hidden';
  });
};


/*
* Modal component 
*/
const Modal = forwardRef<HTMLDivElement, ModalProps>(({
  isOpen,
  onClose,
  title,
  children,
  className = '',
  closeOnOverlayClick = true,
  closeOnEscape = true,
  showCloseButton = true,
  size = 'medium',
  initialFocusRef,
  returnFocusRef,
  ariaDescribedBy,
  ariaLabelledBy,
  testId,
}, ref) => {
  
  const modalRef = useRef<HTMLDivElement | null>(null);
  const previousActiveElement = useRef<Element | null>(null);
  const previousBodyOverflow = useRef<string>('');
  
  const generatedTitleId = useId();
  const generatedContentId = useId();
  const titleId = ariaLabelledBy || generatedTitleId;
  const contentId = ariaDescribedBy || generatedContentId;
  
  useEffect(() => {
    if (isOpen) {
      previousActiveElement.current = document.activeElement;
    }
  }, [isOpen]);
  
  useEffect(() => {
    if (!isOpen || !modalRef.current) return;

    const elementToFocus = initialFocusRef?.current || modalRef.current;
    const nodeToFocusOnClose = returnFocusRef?.current ?? null;

    if (elementToFocus) {
      requestAnimationFrame(() => {
        elementToFocus.focus();
      });
    }

    previousBodyOverflow.current = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = previousBodyOverflow.current;

      if (nodeToFocusOnClose) {
        nodeToFocusOnClose.focus();
      } else if (isHTMLElement(previousActiveElement.current)) {
        previousActiveElement.current.focus();
      }
    };
  }, [isOpen, initialFocusRef, returnFocusRef]);
  
  useEffect(() => {
    if (!isOpen || !closeOnEscape) return;
    
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose, closeOnEscape]);
  
  useEffect(() => {
    if (!isOpen || !modalRef.current) return;
    
    const modal = modalRef.current;
    
    const handleFocusTrap = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;
      
      const focusableElements = getFocusableElements(modal);
      if (focusableElements.length === 0) return;
      
      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];
      
      if (e.shiftKey && document.activeElement === firstElement) {
        e.preventDefault();
        lastElement.focus();
      } else if (!e.shiftKey && document.activeElement === lastElement) {
        e.preventDefault();
        firstElement.focus();
      }
    };
    
    document.addEventListener('keydown', handleFocusTrap);
    return () => document.removeEventListener('keydown', handleFocusTrap);
  }, [isOpen]);
  
  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!closeOnOverlayClick) return;
    
    const clickedElement = e.target as HTMLElement;
    
    if (modalRef.current && modalRef.current.contains(clickedElement)) {
      return;
    }
    
    onClose();
  };
  
  const mergeRefs = (node: HTMLDivElement | null) => {
    modalRef.current = node;
    if (typeof ref === 'function') {
      ref(node);
    } else if (ref) {
      assignDivRef(ref, node);
    }
  };
  
  if (!isOpen) return null;
  
  const modalContent = (
    <div 
      className="thn-modal-overlay" 
      onClick={closeOnOverlayClick ? handleOverlayClick : undefined}
      aria-hidden={!isOpen}
    >
      <div
        ref={mergeRefs}
        className={`thn-modal thn-modal--${size} ${className}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={contentId}
        tabIndex={-1}
        data-testid={testId}
        onClick={(e) => e.stopPropagation()}
      >
        {showCloseButton && (
          <button
            type="button"
            className="thn-modal__close"
            onClick={onClose}
            aria-label={__('Fermer')}
            data-testid={testId ? `${testId}-close` : 'modal-close'}
          >
            <span aria-hidden="true">×</span>
          </button>
        )}
        
        <div className="thn-modal__header">
          <h3 id={titleId}>{title}</h3>
        </div>
        
        <div id={contentId} className="thn-modal__body">
          {children}
        </div>
      </div>
    </div>
  );
  
  return createPortal(modalContent, document.body);
});

Modal.displayName = 'Modal';

export default Modal;

